import React from "react";
import { Box, TextField } from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import { useTheme } from "@mui/material/styles";

import {
  RectangularTextButton,
  RoundIconButton,
} from "components/Base/Buttons";

interface SimpleListControllerProps {
  search: string;
  onSearchChange: (value: string) => void;
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onAction: () => void;
  actionLabel?: string;
  loadingAction?: boolean;
  externalLink?: string;

  /**
   * Optional overrides. Prefer leaving these undefined so RouteTheme controls styling.
   */
  primaryColor?: string;
  textColor?: string;
}

const SimpleListController: React.FC<SimpleListControllerProps> = ({
  search,
  onSearchChange,
  selectedCount,
  totalCount,
  onSelectAll,
  onAction,
  actionLabel = "Perform Action",
  loadingAction = false,
  externalLink,
  primaryColor: primaryColorProp,
  textColor: textColorProp,
}) => {
  const theme = useTheme();

  const primaryColor = primaryColorProp ?? theme.palette.primary.main;

  const textColor =
    textColorProp ?? theme.palette.getContrastText(primaryColor);

  const isAllSelected = selectedCount === totalCount && totalCount > 0;

  return (
    <>
      {/* Search + External link */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <TextField
          fullWidth
          size="small"
          label="Search..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />

        {externalLink && (
          <RoundIconButton
            component="a"
            href={externalLink}
            target="_blank"
            rel="noopener noreferrer"
            colour="coloured"
            displaySize="separate"
            icon={<LaunchIcon />}
            primaryColor={primaryColor}
            iconColor={textColor}
          />
        )}
      </Box>

      {/* Select all + Action button */}
      <Box display="flex" justifyContent="space-between" mb={1}>
        <RectangularTextButton
          colour="clear"
          primaryColor={primaryColor}
          textColor={primaryColor}
          borderColor={primaryColor}
          buttonSize="small"
          onClick={onSelectAll}
        >
          {isAllSelected ? "Deselect All" : "Select All"}
        </RectangularTextButton>

        <RectangularTextButton
          colour="highlighted"
          primaryColor={primaryColor}
          textColor={textColor}
          buttonSize="small"
          onClick={onAction}
          disabled={selectedCount === 0 || loadingAction}
        >
          {loadingAction ? "Processing..." : actionLabel}
        </RectangularTextButton>
      </Box>
    </>
  );
};

export default SimpleListController;